/* Instant cart-drawer upsell.

   Customily's own upsell asks its relations API (~2.4 s, not cacheable) only
   after the add to cart and then waits on its own timers and cart reads before
   it even asks for the personalized previews, so its cards arrive seconds after
   the drawer opens.

   1. Prefetch: on a Customily product page, once the page is idle, read the
      same relations once and keep the few fields the cards need in
      sessionStorage (30 min), plus warm the small card photos.
   2. Instant cards: when the drawer renders with a personalized line, the same
      "You Might Also Like" cards are drawn from that cache before the first
      paint. Customily's widget stays mounted out of sight: our "Add to cart"
      presses its button for that product, so the add carries the design.
   3. Previews: right away we ask Customily's preview generator (the same call
      its widget makes) for the shopper's design on each card, and fade each
      preview in as it lands. Previews are kept per design, so a re-render shows
      them at once. If Customily's hidden widget has the design first, its
      preview URLs fill our cards instead, so this is never slower than
      Customily alone and the cards never change look.

   Never runs during page load, skips Save-Data, and gives up quietly on a slow
   or failed request. */
(() => {
  const CACHE_PREFIX = 'gs_cart_upsell_v2_';
  const LAST_KEY = `${CACHE_PREFIX}last`;
  const PREVIEW_KEY = `${CACHE_PREFIX}previews`;
  const CACHE_TTL_MS = 30 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 8000;
  const PREVIEW_TIMEOUT_MS = 20000;
  const HANDOFF_WAIT_MS = 8000;
  const SHOP = 'golfswagllc.myshopify.com';
  const PREVIEW_URL = 'https://personalized-preview.customily.com/v2/generate-previews';
  const productId = document.currentScript?.dataset.gsUpsellProductId;

  // sessionStorage can be blocked (private mode, policies): never break the page.
  function readJson(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || 'null');
    } catch (_) {
      return null;
    }
  }

  function writeJson(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_) { /* full or blocked: Customily's widget still works */ }
  }

  /* ---------- 1. Prefetch on the product page ---------- */

  function readCache(id) {
    const entry = readJson(CACHE_PREFIX + id);
    return entry && Date.now() - entry.savedAt < CACHE_TTL_MS ? entry : null;
  }

  // Shopify CDN photo at card size.
  function cardImage(src) {
    if (!src || !/cdn\.shopify\.com/.test(src)) return src || '';
    return `${src}${src.includes('?') ? '&' : '?'}width=300`;
  }

  async function prefetch(id) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(
        `https://sh.customily.com/api/products/${encodeURIComponent(id)}/relations?shop=${SHOP}`,
        { credentials: 'omit', signal: controller.signal }
      );
      if (!response.ok) return;
      const data = await response.json();
      const products = (Array.isArray(data?.products) ? data.products : []).map(product => ({
        id: String(product.id),
        handle: product.handle,
        title: product.title,
        image: cardImage(product.image),
        price: product.price,
        variantId: String(product.variantId || ''),
        templateId: product.templateId || '',
      })).filter(product => product.id && product.handle);
      writeJson(CACHE_PREFIX + id, { title: data?.sectionTitle || 'You Might Also Like', products, savedAt: Date.now() });
      products.slice(0, 4).forEach(product => {
        if (!product.image) return;
        const image = new Image();
        image.fetchPriority = 'low';
        image.src = product.image;
      });
    } catch (_) {
      // Slow or failed: the drawer falls back to Customily's own widget.
    } finally {
      clearTimeout(timer);
    }
  }

  function whenIdle(fn) {
    const run = () => ('requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1500));
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  if (productId) {
    try { sessionStorage.setItem(LAST_KEY, productId); } catch (_) { /* storage blocked */ }
    if (!navigator.connection?.saveData && !readCache(productId)) whenIdle(() => prefetch(productId));
  }

  /* ---------- 3. Personalized previews (used by the cards below) ---------- */

  const previewRequests = new Map();

  function readPreviews(customizationId) {
    return readJson(PREVIEW_KEY)?.[customizationId] || {};
  }

  const PREVIEW_ATTEMPTS = 3;
  const PREVIEW_RETRY_MS = 1000;

  async function requestPreviewsOnce(customizationId, templateIds, signal) {
    const response = await fetch(PREVIEW_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ products: templateIds, customizationId, beta: 'false' }),
      credentials: 'omit',
      signal,
    });
    const data = response.ok ? await response.json() : null;
    const previews = {};
    (data?.previews || []).forEach(preview => {
      if (preview?.templateId && preview.previewPath) previews[preview.templateId] = preview.previewPath;
    });
    return previews;
  }

  // One request per design: { templateId: previewUrl }. Sent as early as the
  // add to cart, the design may not be saved yet, so a failed or empty answer
  // is retried a couple of times.
  function loadPreviews(customizationId, templateIds) {
    if (previewRequests.has(customizationId)) return previewRequests.get(customizationId);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PREVIEW_TIMEOUT_MS);
    const request = (async () => {
      for (let attempt = 1; attempt <= PREVIEW_ATTEMPTS; attempt += 1) {
        const previews = await requestPreviewsOnce(customizationId, templateIds, controller.signal).catch(() => ({}));
        if (Object.keys(previews).length) {
          const all = readJson(PREVIEW_KEY) || {};
          all[customizationId] = { ...all[customizationId], ...previews };
          writeJson(PREVIEW_KEY, all);
          return previews;
        }
        if (controller.signal.aborted || attempt === PREVIEW_ATTEMPTS) break;
        await new Promise(resolve => setTimeout(resolve, PREVIEW_RETRY_MS));
      }
      return {};
    })().finally(() => {
      clearTimeout(timer);
      previewRequests.delete(customizationId);
    });
    previewRequests.set(customizationId, request);
    return request;
  }

  // The moment Customily submits the add to cart, its hidden inputs carry the
  // design link: ask for the previews right then, in parallel with the add, so
  // they may be ready before the drawer even opens.
  document.addEventListener('submit', event => {
    const url = event.target?.querySelector?.('[name="properties[_customily-customization-url]"]')?.value;
    const entry = productId && readCache(productId);
    if (!url || !entry) return;
    let customizationId = '';
    try { customizationId = new URL(url, window.location.origin).searchParams.get('customizationId') || ''; } catch (_) { return; }
    const templateIds = [...new Set(entry.products.map(product => product.templateId).filter(Boolean))];
    if (customizationId && templateIds.length) loadPreviews(customizationId, templateIds);
  }, true);

  // Where each product's print area sits in its photo, so the reveal opens on
  // the design. Anything not listed opens from the center.
  const REVEAL_ORIGINS = {
    'the-maggy-magnet-towel': '64% 50%',
    'pitchfix-original-divot-tool': '50% 53%',
  };
  const DONE_LABEL_MS = 1500;
  const ADD_LABEL = 'Add to cart';

  // "Adding design…" loader: dim + light sweep, a chip with the shopper's
  // uploaded image (an "Aa" badge for text designs), and a bar that eases to
  // 90% and waits for the preview.
  function buildLoader(upload) {
    const loader = el('span', 'gs-cart-upsell__loader');
    const chip = el('span', 'gs-cart-upsell__chip');
    const badge = el('span', 'gs-cart-upsell__thumb', 'Aa');
    if (upload) {
      const image = Object.assign(document.createElement('img'), { alt: '', width: 20, height: 20 });
      image.addEventListener('load', () => badge.replaceChildren(image), { once: true }); // "Aa" stays if it fails
      image.src = upload;
    }
    chip.append(badge, el('span', 'gs-cart-upsell__chip-label', 'Adding design…'));
    loader.append(el('span', 'gs-cart-upsell__dim'), el('span', 'gs-cart-upsell__sweep'), chip, el('span', 'gs-cart-upsell__bar'));
    return loader;
  }

  function removeLoader(card) {
    const loader = card.querySelector('.gs-cart-upsell__loader');
    if (!loader) return;
    loader.classList.add('is-leaving');
    setTimeout(() => loader.remove(), 350);
  }

  // Reveal the shopper's design over the stock photo once it has decoded:
  // a wipe out from the print area with a small pop, the bar completes and
  // the chip reads "Your design ✓" before the loader fades.
  function showPreview(card, url) {
    if (!url || card.dataset.preview === url) return;
    card.dataset.preview = url;
    const image = new Image();
    image.src = url;
    (image.decode ? image.decode() : Promise.resolve()).then(() => {
      if (!card.isConnected || card.dataset.preview !== url) return;
      const loader = card.querySelector('.gs-cart-upsell__loader');
      const layer = Object.assign(document.createElement('img'), {
        src: url, alt: '', className: 'gs-cart-upsell__preview', width: 160, height: 160,
      });
      card.querySelector('.gs-cart-upsell__image').insertBefore(layer, loader);
      requestAnimationFrame(() => requestAnimationFrame(() => layer.classList.add('is-in')));
      card.classList.remove('is-loading');
      if (!loader) return;
      card.classList.add('is-done');
      const bar = loader.querySelector('.gs-cart-upsell__bar');
      const from = getComputedStyle(bar).transform;
      bar.style.animation = 'none';
      bar.animate?.([
        { transform: from === 'none' ? 'scaleX(0)' : from, opacity: 1 },
        { transform: 'scaleX(1)', opacity: 1, offset: 0.5 },
        { transform: 'scaleX(1)', opacity: 0 },
      ], { duration: 700, easing: 'ease-out', fill: 'forwards' });
      loader.querySelector('.gs-cart-upsell__chip-label').textContent = 'Your design ✓';
      setTimeout(() => removeLoader(card), DONE_LABEL_MS);
    }, () => { delete card.dataset.preview; });
  }

  /* ---------- 2. Instant cards in the drawer ---------- */

  const drawer = document.querySelector('cart-drawer');
  const CUSTOMILY = '.csh-upsell-cart-container';

  const el = (tag, className, text) => Object.assign(document.createElement(tag), { className, textContent: text || '' });

  // The design of the newest personalized line (Edit Personalization link).
  function customizationIdOf(form) {
    const href = form.querySelector('.product-edit-btn')?.getAttribute('href');
    try {
      return href ? new URL(href, window.location.origin).searchParams.get('customizationId') || '' : '';
    } catch (_) {
      return '';
    }
  }

  // Customily's own card shape, under our class names (Customily skips its
  // widget when a .csh-upsell-cart-container already exists).
  function buildBlock(entry, inCart, customizationId, upload) {
    const products = entry.products.filter(product => !inCart.has(product.variantId));
    if (!products.length) return null;
    const previews = customizationId ? readPreviews(customizationId) : {};
    const block = el('div', 'gs-cart-upsell');
    block.dataset.gsCartUpsell = '';
    const list = el('ul', 'gs-cart-upsell__list');
    products.forEach(product => {
      const card = el('li', 'gs-cart-upsell__card');
      card.dataset.handle = product.handle;
      card.dataset.templateId = product.templateId;
      card.style.setProperty('--gs-reveal-origin', REVEAL_ORIGINS[product.handle] || '50% 50%');
      const saved = previews[product.templateId];
      const media = el('div', 'gs-cart-upsell__image');
      media.append(Object.assign(document.createElement('img'), { src: saved || product.image, alt: product.title, width: 160, height: 160 }));
      if (saved) card.dataset.preview = saved;
      else if (customizationId) {
        card.classList.add('is-loading');
        media.append(buildLoader(upload));
      }
      const name = el('a', 'gs-cart-upsell__name', product.title);
      name.href = `/products/${product.handle}`;
      const button = el('button', 'gs-cart-upsell__btn', ADD_LABEL);
      button.type = 'button';
      button.dataset.handle = product.handle;
      card.append(media, name, el('span', 'gs-cart-upsell__price', `$${product.price}`), button);
      list.append(card);
    });
    const scroller = el('div', 'gs-cart-upsell__scroller');
    scroller.append(list);
    block.append(el('h2', 'gs-cart-upsell__title', 'Complete the set'), scroller);
    return block;
  }

  function requestPreviews(customizationId) {
    const pending = [...drawer.querySelectorAll('.gs-cart-upsell__card.is-loading')];
    const templateIds = [...new Set(pending.map(card => card.dataset.templateId).filter(Boolean))];
    if (!templateIds.length) return;
    loadPreviews(customizationId, templateIds).then(previews => {
      drawer.querySelectorAll('.gs-cart-upsell__card.is-loading').forEach(card => showPreview(card, previews[card.dataset.templateId]));
    });
    // No design in time: the loader leaves and the stock photo stays.
    setTimeout(() => {
      pending.filter(card => card.classList.contains('is-loading')).forEach(card => {
        card.classList.remove('is-loading');
        removeLoader(card);
      });
    }, PREVIEW_TIMEOUT_MS);
  }

  // The products of Customily's hidden widget (same lookup as
  // customily-upsell-visibility.js): each gets its preview URL in `image`
  // with `loading: false` once Customily has the shopper's design.
  function customilyProducts() {
    const pending = [drawer.querySelector(CUSTOMILY)?.__vue__].filter(Boolean);
    const seen = new Set();
    while (pending.length) {
      const component = pending.shift();
      if (!component || seen.has(component)) continue;
      seen.add(component);
      if (Array.isArray(component.products) && typeof component.updateImagePreview === 'function') return component.products;
      pending.push(...(component.$children || []));
    }
    return [];
  }

  // Whichever has the design first fills our cards: our own request or
  // Customily's widget. The URL is loaded directly, so the widget can stay
  // hidden (its own images only load on screen).
  function borrowCustomilyPreviews(ours) {
    const waiting = [...ours.querySelectorAll('.gs-cart-upsell__card:not([data-preview])')];
    if (!waiting.length) return;
    customilyProducts().forEach(product => {
      if (product.loading || !product.image) return;
      const card = waiting.find(item => item.dataset.handle === product.handle);
      if (card) showPreview(card, product.image);
    });
  }

  function syncDrawer() {
    const form = drawer.querySelector('#CartDrawer-Form');
    const ours = form?.querySelector('[data-gs-cart-upsell]');
    // Customily's widget stays out of sight only while our cards stand in for it.
    drawer.toggleAttribute('data-gs-upsell-active', Boolean(ours));
    if (ours) borrowCustomilyPreviews(ours);
    if (!form || ours) return;
    if (form.dataset.gsCartUpsellDone === 'true') return;
    form.dataset.gsCartUpsellDone = 'true'; // one block per drawer render
    if (!form.querySelector('.product-edit-btn')) return; // no personalized line
    let last = null;
    try { last = sessionStorage.getItem(LAST_KEY); } catch (_) { /* storage blocked */ }
    const entry = last && readCache(last);
    if (!entry) return;
    const inCart = new Set([...form.querySelectorAll('.cart-item[data-variant-id]')].map(row => row.dataset.variantId));
    const customizationId = customizationIdOf(form);
    // The uploaded image of that personalized line (line-item.liquid), if any.
    const upload = form.querySelector('.product-edit-btn')?.closest('.cart-item')?.dataset.gsDesignUpload;
    const block = buildBlock(entry, inCart, customizationId, upload);
    if (!block) return;
    form.append(block);
    drawer.setAttribute('data-gs-upsell-active', '');
    if (customizationId) requestPreviews(customizationId);
  }

  // Our button presses Customily's button for the same product (the add that
  // carries the design); without one in time, the product page instead.
  async function onAdd(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.textContent = 'Adding…';
    const started = Date.now();
    let target = null;
    while (!target && Date.now() - started < HANDOFF_WAIT_MS) {
      target = [...drawer.querySelectorAll(`${CUSTOMILY} .csh-product-card`)]
        .find(card => card.querySelector('.csh-product-title')?.getAttribute('href')?.startsWith(`/products/${button.dataset.handle}?`))
        ?.querySelector('.csh-product-btn:not(.csh-product-btn-added)');
      if (!target) await new Promise(resolve => setTimeout(resolve, 250));
    }
    if (!target) {
      window.location.assign(`/products/${button.dataset.handle}`);
      return;
    }
    // customily-upsell-visibility.js adds it with the design; a successful add
    // re-renders the drawer. If nothing happens, let the shopper try again.
    target.click();
    setTimeout(() => {
      if (!button.isConnected) return;
      button.disabled = false;
      button.textContent = ADD_LABEL;
    }, 10000);
  }

  if (drawer) {
    // Drawer renders replace its markup; this runs before that frame paints.
    new MutationObserver(syncDrawer).observe(drawer, { childList: true, subtree: true });
    drawer.addEventListener('click', event => {
      const button = event.target.closest('.gs-cart-upsell__btn');
      if (button) onAdd(button);
    });
    syncDrawer();
  }

  window.GSCartUpsell = { readCache };
})();
